/**
 * Configuração de Ambiente
 * 
 * Este arquivo centraliza todas as configurações de URL para os 3 ambientes:
 * 
 * 1. LOCALHOST (Desenvolvimento Local)
 *    - Frontend: http://localhost:5173
 *    - Backend:  http://localhost:8000
 *    - SSO:      http://localhost:8000/sso/azure
 * 
 * 2. DEMANDASV3 (Homologação - Rede Interna)
 *    - Frontend: http://demandasv3-hmg.fieac.org.br ou http://10.4.16.17:5173
 *    - Backend:  https://demandasv3-hmg.fieac.org.br:8000
 *    - SSO:      https://demandasv3-hmg.fieac.org.br:8000/sso/azure
 * 
 * 3. HERMES (Produção)
 *    - Frontend: https://hermes.fieac.org.br
 *    - Backend:  https://apihermes.fieac.org.br
 *    - SSO:      https://apihermes.fieac.org.br/sso/azure
 * 
 * NOTA: As rotas SSO estão em web.php (sem prefixo /api) para funcionar com sessões
 */

export type Environment = 'localhost' | 'demandasv3' | 'hermes';

interface EnvironmentConfig {
  name: Environment;
  apiBaseUrl: string;      // URL base da API (com /api)
  backendUrl: string;      // URL do backend (sem /api, para SSO)
  ssoUrl: string;          // URL completa do SSO
  frontendUrl: string;     // URL do frontend
}

/**
 * Detecta o ambiente atual baseado no hostname
 */
export const detectEnvironment = (): Environment => {
  const hostname = window.location.hostname;

  // Produção: hermes.fieac.org.br
  if (hostname === 'hermes.fieac.org.br') {
    return 'hermes';
  }

  // Homologação: demandasv3-hmg.fieac.org.br ou IP da rede
  if (
    hostname === 'demandasv3-hmg.fieac.org.br' ||
    hostname === '10.4.16.17' ||
    hostname.includes('demandasv3')
  ) {
    return 'demandasv3';
  }

  // Desenvolvimento local (localhost, 127.0.0.1, ou qualquer outro)
  return 'localhost';
};

/**
 * Configurações para cada ambiente
 */
const environments: Record<Environment, EnvironmentConfig> = {
  // Desenvolvimento Local
  localhost: {
    name: 'localhost',
    apiBaseUrl: 'http://localhost:8000/api',
    backendUrl: 'http://localhost:8000',
    ssoUrl: 'http://localhost:8000/sso/azure',
    frontendUrl: 'http://localhost:5173',
  },

  // Homologação (Rede Interna)
  demandasv3: {
    name: 'demandasv3',
    apiBaseUrl: 'https://demandasv3-hmg.fieac.org.br:8000/api',
    backendUrl: 'https://demandasv3-hmg.fieac.org.br:8000',
    ssoUrl: 'https://demandasv3-hmg.fieac.org.br:8000/sso/azure',
    frontendUrl: 'https://demandasv3-hmg.fieac.org.br',
  },

  // Produção
  hermes: {
    name: 'hermes',
    apiBaseUrl: 'https://apihermes.fieac.org.br/api',
    backendUrl: 'https://apihermes.fieac.org.br',
    ssoUrl: 'https://apihermes.fieac.org.br/sso/azure',
    frontendUrl: 'https://hermes.fieac.org.br',
  },
};

/**
 * Obtém a configuração do ambiente atual
 */
export const getEnvironmentConfig = (): EnvironmentConfig => {
  const env = detectEnvironment();
  return environments[env];
};

/**
 * Obtém a URL base da API
 */
export const getApiBaseUrl = (): string => {
  return getEnvironmentConfig().apiBaseUrl;
};

/**
 * Obtém a URL do backend (sem /api)
 */
export const getBackendUrl = (): string => {
  return getEnvironmentConfig().backendUrl;
};

/**
 * Obtém a URL do SSO
 */
export const getSsoUrl = (): string => {
  return getEnvironmentConfig().ssoUrl;
};

/**
 * Obtém a URL do frontend
 */
export const getFrontendUrl = (): string => {
  return getEnvironmentConfig().frontendUrl;
};

/**
 * Verifica se está em ambiente de desenvolvimento
 */
export const isDevelopment = (): boolean => {
  return detectEnvironment() === 'localhost';
};

/**
 * Verifica se está em ambiente de homologação
 */
export const isHomologation = (): boolean => {
  return detectEnvironment() === 'demandasv3';
};

/**
 * Verifica se está em ambiente de produção
 */
export const isProduction = (): boolean => {
  return detectEnvironment() === 'hermes';
};

// Exporta a configuração atual para uso direto
export const ENV_CONFIG = getEnvironmentConfig();

// Log do ambiente detectado (apenas em desenvolvimento)
if (typeof window !== 'undefined' && isDevelopment()) {
  const config = getEnvironmentConfig();
  // eslint-disable-next-line no-console
  console.info(`🌍 Ambiente: ${config.name.toUpperCase()} | API: ${config.apiBaseUrl}`);
}
